<template>
  <div id="app">
    <!-- ⭐ 添加 key，强制 Vue 重新渲染 -->
    <div id="nav" :key="navKey">
      <router-view v-if="$route.meta.isLogin"></router-view>
      <admin-home v-if="$route.meta.isAdmin" />
      <Home v-if="Showhome" />
    </div>

    <div 
      class="language-switcher" 
      v-if="showLanguageSwitcher" 
      :class="{ 'language-switcher-mobile': windowWidth < 850 }"
    >
      <button @click="switchLanguage('en')" :class="{ active: currentLang === 'en' }">
        EN
      </button>
      <span class="divider">|</span>
      <button @click="switchLanguage('zh-CN')" :class="{ active: currentLang === 'zh-CN' }">
        中文
      </button>
    </div>
  </div>
</template>

<script>
import Home from "@/views/Home.vue";
import AdminHome from "@/views/Home1.vue";

export default {
  name: 'App',
  components: { Home, AdminHome },
  data() {
    return {
      Showhome: false,
      // ⭐ 修改：使用 sessionStorage 替代 localStorage
      currentLang: sessionStorage.getItem('preferred_language') || 'en',
      translateReady: false,
      navKey: 0,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    showLanguageSwitcher() {
      return !(this.$route.meta.isAdmin || this.$route.meta.isLogin);
    },
  },
  watch: {
    $route() {
      this.Showhome = !(this.$route.meta.isAdmin || this.$route.meta.isLogin);
      if (this.translateReady) {
        this.$nextTick(() => {
          try {
            if (this.currentLang === 'zh-CN') {
              window.translate.execute();
            }
          } catch (e) {
            console.warn('路由变化重新翻译失败:', e);
          }
        });
      }
    },
  },
  methods: {
    // 处理窗口大小变化
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // 初始化 translate.js
    initTranslate() {
      if (!window.translate) {
        if (!this._retryCount) this._retryCount = 0;
        if (this._retryCount < 10) {
          this._retryCount++;
          setTimeout(this.initTranslate, 300);
        } else {
          console.error('translate.js 加载超时');
        }
        return;
      }

      window.translate.language.setLocal('english');

      // ⭐ 根据 sessionStorage 设置目标语言
      const targetLang = this.currentLang === 'zh-CN' ? 'chinese_simplified' : 'english';
      window.translate.to = targetLang;
      window.translate.storage.set('to', targetLang);

      window.translate.service.use('client.edge');
      window.translate.listener.start();

      this.translateReady = true;
      this._retryCount = 0;

      this.$nextTick(() => {
        try {
          if (this.currentLang === 'zh-CN') {
            window.translate.execute();
          }
          console.log('✅ 翻译初始化完成，目标语言:', targetLang);
        } catch (e) {
          console.warn('首次翻译执行失败:', e);
        }
      });
    },

    // 切换语言
    switchLanguage(lang) {
      if (this.currentLang === lang || !this.translateReady) return;

      // 如果切换到英文
      if (lang === 'en') {
        this.currentLang = lang;
        // ⭐ 修改：保存到 sessionStorage
        sessionStorage.setItem('preferred_language', lang);

        // 强制重新渲染 #nav 组件
        this.navKey++;
        
        // 等待 Vue 重新渲染完成
        this.$nextTick(() => {
          // 重置 translate.js 状态
          window.translate.to = 'english';
          window.translate.storage.set('to', 'english');
          window.translate.language.setLocal('english');
          
          if (window.translate.cache) {
            window.translate.cache = {};
          }
          
          try {
            window.translate.execute();
          } catch (e) {
            console.warn('切换回英文失败:', e);
          }
        });
        return;
      }

      // 切换到中文
      this.currentLang = lang;
      // ⭐ 修改：保存到 sessionStorage
      sessionStorage.setItem('preferred_language', lang);

      const targetLang = 'chinese_simplified';
      window.translate.to = targetLang;
      window.translate.storage.set('to', targetLang);

      this.$nextTick(() => {
        try {
          window.translate.execute();
        } catch (e) {
          console.warn('切换到中文失败:', e);
        }
      });
    },
  },
  mounted() {
    this.initTranslate();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss">
#app {
  font-family: Cotyledon-Regular, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: #fff;
  display: flex;
  justify-content: space-around;
}

#nav {
  width: 100%;
  min-height: 100vh;
  background-color: #f8fafc;
}

.language-switcher {
  position: fixed;
  top: 20px;
  right: 30px;
  z-index: 9999;
  background: #ffffff;
  padding: 6px 14px;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  border: 1px solid #e8ecf1;
}

.language-switcher button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #8898aa;
  padding: 4px 8px;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-family: inherit;
}

.language-switcher button:hover {
  color: #2c3e50;
  background: #f0f2f5;
}

.language-switcher button.active {
  color: #007bff;
  background: #e8f0fe;
}

.language-switcher .divider {
  color: #dce1e8;
  font-size: 14px;
}

// 隐藏 translate.js 自带的翻译工具条
#translate {
  display: none !important;
}

// 移动端样式
.language-switcher-mobile {
  top: 80px !important;
  right: 20px !important;
  padding: 4px 12px !important;
  font-size: 12px !important;
}

// 更小屏幕的进一步优化
@media (max-width: 480px) {
  .language-switcher-mobile {
    top: 10px !important;
    right: 80px !important;
    padding: 4px 10px !important;
    font-size: 11px !important;
  }
}
</style>